import { useCallback, useEffect, useRef, useState } from "react";
import { Layout } from "@/components/layout";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";
import { container } from "@/lib/utils";
import { EmptyVisits } from "@/components/ui/visits/empty-visits";
import { VisitList } from "@/components/ui/visits/visits-list";
import { useTranslation } from "react-i18next";
import { NewVisitSidebar } from "@/components/ui/visits/new-visit-sidebar";
import { useAuthStore } from "@/store/auth.store";
import { useVisitsStore } from "@/store/visits.store";
import { useLocation, useRouter } from "@tanstack/react-router";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { useRecordingUsageStore } from "@/store/recordingUsage.store";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { REFRESH_AUDIO_INTERVAL } from "@/common/config";
import { OnboardingCard } from "@/components/ui/onboarding/onboarding-card";
import { isOnboarded, isPostStepSeen } from "@/lib/onboarding";
import { usePostOnboardingStore } from "@/store/postOnboarding.store";

export const Visits = (): JSX.Element => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(() => !isOnboarded());
  const [postStepOpen, setPostStepOpen] = useState(false);
  const firstRecordingDone = usePostOnboardingStore((state) => state.done);
  const previousFirstRecordingDoneRef = useRef(firstRecordingDone);
  const { userData } = useAuthStore.getState();
  const { getFiles, files, loading } = useVisitsStore();
  const router = useRouter();
  const location = useLocation();
  const { fetchAndSetAvailableMinutes } = useRecordingUsageStore.getState();

  useEffect(() => {
    const previous = previousFirstRecordingDoneRef.current;
    previousFirstRecordingDoneRef.current = firstRecordingDone;
    if (!previous && firstRecordingDone && isOnboarded() && !isPostStepSeen()) {
      setPostStepOpen(true);
    }
  }, [firstRecordingDone]);

  useEffect(() => {
    if (userData?.id) {
      void getFiles({ userId: userData.id, sparse: 1 });
      void fetchAndSetAvailableMinutes(userData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = useCallback(
    (showLoading = true): void => {
      if (userData?.id) {
        void getFiles({ userId: userData.id, sparse: 1 }, showLoading);
      }
    },
    [userData?.id, getFiles]
  );

  useEffect(() => {
    if (!userData) return undefined;
    const intervalTask = setInterval(() => {
      void getFiles({ userId: userData.id, sparse: 1 }, false);
    }, REFRESH_AUDIO_INTERVAL);
    return (): void => {
      clearInterval(intervalTask);
    };
  }, [userData, getFiles]);

  useEffect(() => {
    if (location.search.openNewVisit == true) {
      setSidebarOpen(true);
      router.history.replace(location.pathname, {});
    }
  }, [router.history, location.pathname, location.search]);

  return (
    <Layout
      isLoading={loading}
      navbarTitle={t("visits.title")}
      breadcrumbs={[
        { name: t("visits.title"), href: "/visits", current: true },
      ]}
    >
      <div className="min-h-screen p-6">
        <motion.div
          animate="show"
          className="mx-auto max-w-5xl space-y-8"
          initial="hidden"
          variants={container}
        >
          {files?.length === 0 ? (
            <EmptyVisits
              onOpenSidebar={() => {
                setSidebarOpen(true);
              }}
            />
          ) : (
            <div className="p-4">
              <div className="flex justify-end mb-6">
                <SubscriptionOnlyButton
                  validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
                  onClick={() => {
                    setSidebarOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t("visits.newVisitButton")}
                </SubscriptionOnlyButton>
              </div>
              <VisitList
                visits={files}
                refresh={() => {
                  retry(false);
                }}
              />
            </div>
          )}
        </motion.div>
        <NewVisitSidebar open={sidebarOpen} onOpenChange={setSidebarOpen} />
        <OnboardingCard
          open={onboardingOpen || postStepOpen}
          postStep={!onboardingOpen && postStepOpen}
          onOpenChange={(open) => {
            setOnboardingOpen(open);
            setPostStepOpen(open);
          }}
          onStartRecording={() => {
            setSidebarOpen(true);
          }}
        />
      </div>
    </Layout>
  );
};
